import { Component, inject, input, OnChanges, OnInit, output, SimpleChanges } from '@angular/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import { UserIconComponent } from '../user-icon/user-icon.component';
import { MatIcon } from '@angular/material/icon';
import { ButtonComponent } from '../../../common/components/button/button.component';
import { MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { Router } from '@angular/router';
import { LastMessageService } from '../../../services/lastMessage/last-message.service';
import { DateUtils } from '../../../services/util';
import { FormsModule } from '@angular/forms';
import { MsgType } from 'matrix-js-sdk';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { DirectMessage } from '../../../../shared-api/directMessage';
import { MessageService } from '../../../services/message/message.service';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatrixService } from '../../../services/matrix/matrix.service';
import { RoomMember } from '../../../model/room-member';
import { Room as ModelRoom } from '../../../model/room';
import { PresenceService, PresenceStatus } from '../../../services/presence/presence.service';
import { map, Observable } from 'rxjs';
import { RoomListAccessService } from '../../../services/room-list/room-list-access.service';
import { UserProfileCardTestIds } from './sb-user-profile-card.test-ids';
import { RoomMembershipState } from '../../../../shared-api/roomMembershipState';
import { CleanReplyPipe } from '../../../common/pipes/clean-reply.pipe';
import { MatTooltip } from '@angular/material/tooltip';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';

@Component({
  selector: 'app-sb-user-profile-card',
  imports: [
    UserIconComponent,
    MatIcon,
    ButtonComponent,
    MatIconButton,
    MatCard,
    MatCardTitle,
    MatCardHeader,
    MatCardContent,
    DatePipe,
    FormsModule,
    MatPaginator,
    AsyncPipe,
    MatTooltip
  ],
  templateUrl: './sb-user-profile-card.component.html',
  styleUrl: './sb-user-profile-card.component.scss'
})
/**
 * Zeigt das Benutzerprofil eines Raum-Mitglieds mit Anwesenheitsstatus,
 * gemeinsamen Räumen und Nachrichtenfunktionen an.
 * Begründung: Diese Komponente erfüllt keine AFOs, da sie
 * nur zur Darstellung und einfachen Kommunikation dient.
 */
export class SbUserProfileCardComponent implements OnInit, OnChanges {
  member = input<RoomMember | undefined>();
  isVisible = input(false);
  isHoverPreview = input(false);
  closed = output<void>();
  commonRooms: ModelRoom[] = [];
  showMessageInput = false;
  messageText = '';
  presenceStatus: PresenceStatus = 'offline';
  currentPage = 0;
  pageSize = 3;
  displayedCommonRooms: ModelRoom[] = [];
  hoverPageSize = 2;
  hoverCurrentPage = 0;
  hoverDisplayedCommonRooms: ModelRoom[] = [];

  protected readonly presenceService = inject(PresenceService);
  protected readonly clientService = inject(MatrixService);
  protected readonly router = inject(Router);
  protected readonly lastMessageService = inject(LastMessageService);
  protected readonly messageService = inject(MessageService);
  protected readonly testIds = UserProfileCardTestIds;
  protected readonly DateUtils = DateUtils;

  private readonly roomListAccessService = inject(RoomListAccessService);
  private readonly dialogData = inject(MAT_DIALOG_DATA, { optional: true });
  private readonly snackbarService = inject(SnackbarService);

  protected get effectiveMember(): RoomMember | undefined {
    return this.member() ?? this.dialogData?.member;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['member'] && this.effectiveMember?.mxid) {
      this.updatePresenceAndCommonRooms();
    }
  }

  ngOnInit(): void {
    if (this.effectiveMember?.mxid) {
      this.updatePresenceAndCommonRooms();
    }
  }

  getDisplayName(): string {
    return this.effectiveMember?.rawDisplayName || 'Unknown User';
  }

  getTimAddress(): string {
    return this.effectiveMember?.mxid || '';
  }

  getLastMessageSender$(roomId: string): Observable<string> {
    return this.lastMessageService.lastMessageDict$.pipe(map(dict => dict.get(roomId)?.senderId ?? ''));
  }

  getLastMessagePreview$(roomId: string): Observable<string> {
    return this.lastMessageService.lastMessageDict$.pipe(
      map(dict => {
        const message = dict.get(roomId);
        if (!message) return 'Keine Nachrichten';

        let body = message.body ?? 'Keine Nachrichten';

        const isReply = body.includes('mx-reply') || body.includes('In reply to') || body.includes('&gt;');

        if (isReply) {
          const cleanPipe = new CleanReplyPipe();
          body = cleanPipe.transform(body);
          body = body.replace(/<[a-zA-Z/][^>]*>/g, ' ');
          body = body.replace(/\s+/g, ' ').trim();
        } else {
          body = body
            .replaceAll(/<br\s*\/?>/gi, ' ')
            .replaceAll(/<\/?p>/gi, ' ')
            .replaceAll('\n', ' ');
        }

        return body || 'Keine Nachrichten';
      })
    );
  }

  getRoomTitle$(room: ModelRoom): Observable<string> {
    return this.lastMessageService.lastMessageDict$.pipe(
      map(dict => {
        const lastMessage = dict.get(room.roomId);

        if (lastMessage?.body) {
          const sender = room.members.find(m => m.mxid === lastMessage.senderId);
          // TODO wtf - überprüfen!
          return sender?.rawDisplayName || lastMessage.senderId || room.name;
        }
        return room.name;
      })
    );
  }

  async sendMessage(): Promise<void> {
    if (!this.effectiveMember?.mxid || !this.messageText.trim()) return;

    try {
      const directMessage: DirectMessage = {
        toAccount: this.effectiveMember.mxid,
        body: this.messageText,
        msgtype: MsgType.Text
      };

      const { room_id: roomId } = await this.messageService.sendDirectMessage(directMessage);
      this.closeDialog();
      this.messageText = '';
      await this.router.navigate(['/rooms', roomId]);
    } catch (error) {
      console.error('Failed to send DM:', error);
    }
  }

  toggleMessageInput(): void {
    this.showMessageInput = !this.showMessageInput;
  }

  navigateToRoom(event: MouseEvent, roomId: string): void {
    event.preventDefault();
    event.stopPropagation();

    this.router.navigate(['/rooms', roomId]);
    this.closeDialog();
  }

  handlePageEvent(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.updateDisplayedRooms();
  }

  handleHoverPageEvent(event: PageEvent): void {
    this.hoverCurrentPage = event.pageIndex;
    this.updateHoverDisplayedRooms();
  }

  closeDialog(): void {
    this.closed.emit();
  }

  isInsuredPerson(): boolean {
    const mxId = this.effectiveMember?.mxid || '';
    const localPart = mxId.split(':')[0].replace('@', '');
    return /^[A-Z]\d{9}$/.test(localPart);
  }

  getKVNR(): string {
    const mxId = this.effectiveMember?.mxid || '';
    return mxId.split(':')[0].replace('@', '') || '';
  }

  async copyKVNR(): Promise<void> {
    const kvnr = this.getKVNR();
    if (kvnr) {
      navigator.clipboard
        .writeText(kvnr)
        .then(() => {
          this.snackbarService.success('KVNR kopiert');
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
        });
    }
  }

  protected isCurrentUser(): boolean {
    const currentUserId = this.clientService.getUserId();
    return currentUserId === this.effectiveMember?.mxid;
  }

  private updatePresenceAndCommonRooms(): void {
    this.presenceService.getPresence(this.effectiveMember!.mxid).subscribe({
      next: response => {
        this.presenceStatus = response;
        const currentUserId = this.clientService.getUserId();
        if (currentUserId) {
          this.commonRooms = this.getCommonRooms(currentUserId, this.effectiveMember!.mxid);
          this.updateDisplayedRooms();
          this.updateHoverDisplayedRooms();
        }
      },
      error: error => {
        console.error('Error updating presence:', error);
        this.presenceStatus = 'offline';
        this.commonRooms = [];
        this.displayedCommonRooms = [];
        this.hoverDisplayedCommonRooms = [];
      }
    });
  }

  private updateDisplayedRooms(): void {
    const startIndex = this.currentPage * this.pageSize;
    this.displayedCommonRooms = this.commonRooms.slice(startIndex, startIndex + this.pageSize);
  }

  private updateHoverDisplayedRooms(): void {
    const startIndex = this.hoverCurrentPage * this.hoverPageSize;
    this.hoverDisplayedCommonRooms = this.commonRooms.slice(startIndex, startIndex + this.hoverPageSize);
  }

  /****************************************************************************
   * Hilfsmethoden
   ****************************************************************************/

  private getCommonRooms(userId1: string, userId2: string): ModelRoom[] {
    if (!userId1 || !userId2) return [];

    return this.roomListAccessService
      .getRoomInfosSync()
      .map(roomInfo => roomInfo.modelRoom)
      .filter(room => {
        const member1 = room.members.find(m => m.mxid === userId1);
        const member2 = room.members.find(m => m.mxid === userId2);

        return (
          member1?.membershipState === RoomMembershipState.Join && member2?.membershipState === RoomMembershipState.Join
        );
      });
  }
}
